import { inspect } from 'util'
import React, { Component } from 'react'
import { connect, DispatchProp } from 'react-redux'
import {
  AppState,
  Linking,
  DeviceEventEmitter,
  Dimensions,
  AppStateStatus,
  NativeEventSubscription,
  EmitterSubscription,
} from 'react-native'
import QuickActions from 'react-native-quick-actions'
import OrientationDirector, { Orientation } from 'react-native-orientation-director'
import moment from 'moment'
import { get, noop, pick } from 'lodash'
import { ApolloProvider } from '@apollo/client'
import { AuthorizeResult, RefreshResult } from 'react-native-app-auth'
import Config from 'react-native-config'
import { HotUpdater } from '@hot-updater/react-native'
import { KetoMojo } from '@services'
import HealthKit from '@src/services/HealthKit'
import {
  Device,
  Notification,
  Bugsnag,
  Apollo,
  Stripe,
  App,
  Analytics,
  CustomEventTypes,
} from '@config'
import { User, Intercom, Storage, openUrl } from '@utils'
import { IntercomProvider } from '@containers/Intercom'
import { TimeZoneContext } from '@src/config/timezone'
import { ImpersonationContext } from '@src/config/impersonation'
import { visitedTutorialsSelector } from '@src/selectors/education'
import { onboardingSelector, userSelector } from '@src/selectors/app'
import { ketoMojoUserSelector } from '@src/selectors/integrations'
import { OnboardingState, RootStoreState } from '@src/models/app.types'
import { Sensor, User as UserType } from '@src/types'
import { EducationStoreState } from '@screens/Education/types'
import { SettingsStoreState } from '@src/screens/Settings/models/settings.types'
import StreamChat from '@src/services/StreamChat'
import { handleEngineNotification } from '@src/components/notifications/utils/handler'
import { shouldShowModalOnce } from '@src/hooks/useOneTimeVisitedState'
import { WebPresentationModeProvider } from '@src/config/webPresentationMode'
import { sensorSelector } from '@src/selectors/sensor'
import { settingsSelector } from '@src/selectors/settings'
import { RefreshTokenStatus } from '@src/services/KetoMojo/ketoMojo'

const PushNotification = Device.web ? null : require('react-native-push-notification')

interface AppWithStateProps extends DispatchProp {
  user: UserType | null
  sensor: Sensor | null
  isOnboardingInProgress: boolean
  ketoMojoUser: AuthorizeResult | undefined
  healthKitSync: boolean
  ketoMojoSync: boolean
  visitedTutorials: EducationStoreState['visitedTutorials']
  settings: SettingsStoreState
  onboarding: OnboardingState
}

interface AppWithStateState {
  timeZone: string
  isImpersonating: boolean
}

const NOTIFICATION_TOKEN_EXPIRY_DAYS = 7

export function createAppStateNavigator(WrappedComponent: any) {
  class AppWithState extends Component<AppWithStateProps, AppWithStateState> {
    appState: AppStateStatus | 'launch' | 'login' = AppState.currentState
    appStateChangeListener: NativeEventSubscription | undefined
    quickActionShortcutListener: EmitterSubscription | undefined
    linkingListener: EmitterSubscription | undefined

    constructor(props: AppWithStateProps) {
      super(props)
      this.state = {
        timeZone: moment.tz.guess(true),
        isImpersonating: false,
      }
    }

    setStateAsync = (state: Partial<AppWithStateState>) => {
      return new Promise<void>((resolve) => {
        this.setState(state as Parameters<typeof this.setState>[0], resolve)
      })
    }

    async componentDidMount() {
      const { user } = this.props
      console.log('AppState:componentDidMount', user)
      const isImpersonating = User.isImpersonating()

      Stripe.configure(Apollo.endpointEnv)
      StreamChat.configure(Apollo.endpointEnv)

      await this.setStateAsync({ isImpersonating })

      if (user) {
        this.logFirstLogin(isImpersonating, user)
        Notification.configure(this.handleNotificationToken, this.handleNotification)
        this.handleTimeZone(user)
      }

      if (Device.shouldLockToPortrait(Dimensions.get('window'))) {
        OrientationDirector.lockTo(Orientation.portrait)
      }

      this.checkAppVersion()

      this.appStateChangeListener = AppState.addEventListener('change', this.handleAppStateChange)
      this.quickActionShortcutListener = DeviceEventEmitter.addListener(
        'quickActionShortcut',
        this.handleQuickAction,
      )
      Linking.getInitialURL().then(this.handleOpenURL).catch(noop)
      this.linkingListener = Linking.addEventListener('url', this.handleOpenURL)

      this.handleAppStateChange('launch')
      this.updateConsecutiveDaysAppUsage()

      // process quick actions on cold launch
      QuickActions.popInitialAction().then(this.handleQuickAction).catch(console.error)
    }

    async componentDidUpdate(prevProps: AppWithStateProps) {
      const { user, isOnboardingInProgress } = this.props
      const isAuthorizedPrev = !!prevProps.user
      const isAuthorized = !!user

      if (isAuthorized && isAuthorized !== isAuthorizedPrev) {
        const isImpersonating = User.isImpersonating()

        this.logFirstLogin(isImpersonating, user)
        await this.setStateAsync({ isImpersonating })

        Notification.configure(this.handleNotificationToken, this.handleNotification)
        this.handleTimeZone(user)
        this.handleAppStateChange('login')
      } else if (!isOnboardingInProgress && prevProps.isOnboardingInProgress) {
        this.handleAppStateChange('login')
      }
    }

    componentWillUnmount() {
      console.log('AppState:componentWillUnmount')

      this.appStateChangeListener?.remove()
      this.quickActionShortcutListener?.remove()
      this.linkingListener?.remove()
    }

    syncHealth = async ({ healthKitSync } = this.props) => {
      const { isImpersonating } = this.state
      const isAvailable = Device.hasHealthKit

      if (isImpersonating || !isAvailable) {
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'skipped',
          reason: isImpersonating ? 'impersonating' : 'unavailable',
        })
        return
      }

      const shouldShowPermissionModal = shouldShowModalOnce(Storage.PERMISSIONS_MODAL_VISITED_KEY)

      if (shouldShowPermissionModal) {
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'skipped',
          reason: 'permissions_modal_pending',
        })
        return
      }
      const userId = get(this.props, 'user.id')

      if (!userId) {
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'skipped',
          reason: 'missing_user',
        })
        return
      }

      if (!healthKitSync) {
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'skipped',
          reason: 'sync_disabled',
        })
        return
      }

      try {
        if (!(await HealthKit.isPermissionsStatusDetermined())) {
          await HealthKit.initialize()
        }
      } catch (error: any) {
        Bugsnag.notify(error)
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'skipped',
          reason: 'permissions_failed',
        })
        return
      }

      // Permission requests can finish after the account or sync settings change.
      if (
        this.state.isImpersonating ||
        !this.props.healthKitSync ||
        get(this.props, 'user.id') !== userId
      ) {
        return
      }

      Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
        stage: 'native_upload_dispatched',
        reason: 'native_sync_enabled',
      })
      this.props.dispatch({ type: 'app/updateHealthData' })
    }

    refreshKetoMojoToken = async ({
      syncEnabled,
      integrationUser,
      refreshToken,
      integrationUserKey,
      integrationUserFields = ['refreshToken', 'accessToken', 'accessTokenExpirationDate'],
      updateData,
    }: {
      syncEnabled: boolean
      integrationUser: AuthorizeResult | undefined
      refreshToken: (
        token: string,
      ) => Promise<RefreshResult | RefreshTokenStatus.InvalidSession | null>
      integrationUserKey: 'ketoMojoUser'
      integrationUserFields?: string[]
      updateData: () => void
    }) => {
      if (!syncEnabled || !integrationUser?.accessToken) {
        return
      }

      const isExpired = moment().isAfter(moment(integrationUser.accessTokenExpirationDate))

      if (!isExpired) {
        updateData()
        return
      }

      let user = await refreshToken(integrationUser.refreshToken)

      if (user === RefreshTokenStatus.InvalidSession) {
        this.props.dispatch({
          type: 'settings/updateIntegrationsSyncSettings',
          payload: {
            [integrationUserKey]: null,
          },
        })
        this.props.dispatch({
          type: 'settings/update',
          payload: {
            ketoMojoSync: false,
          },
        })
        return
      }

      if (!user) {
        return
      }

      if (!user.refreshToken) {
        user = {
          ...user,
          refreshToken: integrationUser.refreshToken,
        }
      }

      this.props.dispatch({
        type: 'settings/updateIntegrationsSyncSettings',
        payload: {
          [integrationUserKey]: pick(user, integrationUserFields),
        },
        success: updateData,
      })
    }

    syncKetoMojo = () => {
      const { ketoMojoUser, ketoMojoSync, dispatch } = this.props

      this.refreshKetoMojoToken({
        syncEnabled: ketoMojoSync,
        integrationUser: ketoMojoUser,
        refreshToken: KetoMojo.refreshToken,
        updateData: () => {
          dispatch({
            type: 'app/updateKetoMojoData',
            payload: {
              initial: false,
            },
          })
        },
        integrationUserKey: 'ketoMojoUser',
      })
    }

    refetchDailyData = () => {
      this.props.dispatch({ type: 'app/clearCaches' })
      this.props.dispatch({ type: 'events/fetch' })
      this.props.dispatch({ type: 'dailyCharts/fetch' })
      this.props.dispatch({ type: 'dailyNutrition/fetch' })
    }

    refetchNotifications = () => {
      this.props.dispatch({ type: 'notifications/refetch' })
    }

    // App State
    handleAppStateChange = (nextAppState: AppStateStatus | 'launch' | 'login') => {
      const { user, isOnboardingInProgress } = this.props
      const isAuthorized = !!user

      if (!isAuthorized || isOnboardingInProgress || nextAppState === 'unknown') {
        return
      }

      console.log('AppState:handleAppStateChange:', this.appState, ' -> ', nextAppState)
      if (this.appState.match(/background/) && nextAppState === 'active') {
        console.log('AppState:handleAppStateChange:active')
        if (!Device.web) {
          PushNotification.setApplicationIconBadgeNumber(0)
        }
        this.handleTimeZone(user)
        this.refetchDailyData()
        this.props.dispatch({
          type: 'app/resetCalendars',
          payload: { sensor: this.props.sensor },
        }) // This needs to be run before app/updateAppState
        this.props.dispatch({
          type: 'app/updateAppState',
          payload: {
            lastOpened: moment().toISOString(),
          },
        })
        this.refetchNotifications()
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'trigger',
          trigger: 'foreground',
        })
        this.syncHealth()
        this.syncKetoMojo()
      } else if (
        (this.appState.match(/active|background|unknown/) && nextAppState === 'launch') ||
        (this.appState.match(/active|background|unknown|launch|login/) && nextAppState === 'login')
      ) {
        console.log('AppState:handleAppStateChange:launchOrLogin')
        this.setVisitedTutorials()
        this.props.dispatch({ type: 'app/config' })
        this.props.dispatch({ type: 'users/fetch' })
        this.props.dispatch({ type: 'uiFlags/fetch' })
        this.props.dispatch({ type: 'checklist/fetchOnboardingItems' })
        this.props.dispatch({
          type: 'meals/fetchFavorites',
          payload: { pagination: { page: 1, pageSize: 100 }, filter: { favorite: true } },
        })
        this.props.dispatch({
          type: 'meals/fetchRecentMeals',
          payload: { pagination: { page: 1, pageSize: 100 }, filter: {} },
        })
        this.props.dispatch({
          type: 'activities/fetchFavorites',
          payload: { pagination: { page: 1, pageSize: 100 }, filter: { favorite: true } },
        })
        this.props.dispatch({
          type: 'activities/fetchRecentActivities',
          payload: { pagination: { page: 1, pageSize: 25 }, filter: {} },
        })
        this.refetchNotifications()
        this.props.dispatch({ type: 'app/resetCalendars', payload: { sensor: this.props.sensor } })
        this.props.dispatch({ type: 'nutritionistHub/allEhrAppointments' })
        Analytics.track(CustomEventTypes.HealthKitSyncRouting, {
          stage: 'trigger',
          trigger: nextAppState,
        })
        this.syncHealth()
        this.syncKetoMojo()
      }

      if (nextAppState === 'active') {
        // HotUpdater is configured once at the app entry point via HotUpdater.wrap()
        // in App.tsx (baseURL + updateStrategy), so checkForUpdate() reuses that resolver.
        HotUpdater.checkForUpdate({ updateStrategy: 'appVersion' })
          .then(async (updateInfo) => {
            if (!updateInfo) {
              return
            }
            await updateInfo.updateBundle()
            if (updateInfo.shouldForceUpdate) {
              await HotUpdater.reload()
            }
          })
          .catch((error: any) => {
            Bugsnag.notify(error)
          })
      }

      this.appState = nextAppState
    }

    setVisitedTutorials = () => {
      const { visitedTutorials, dispatch } = this.props

      if (Object.keys(visitedTutorials).length > 0) {
        // we've already restored tutorials from Storage
        return
      }

      const visitedTutorialsFromAsyncStorage = Storage.get(Storage.VISITED_TUTORIALS_KEY, {})
      Object.keys(visitedTutorialsFromAsyncStorage).forEach((key) => {
        dispatch({ type: 'education/setVisitedTutorial', payload: key })
      })
    }

    handleOpenURL = (
      event:
        | string
        | null
        | {
            url: string
          },
    ) => {
      const url = typeof event === 'string' ? event : event?.url
      Analytics.track(CustomEventTypes.UrlOpened, { url })
      Bugsnag.leaveBreadcrumb('Handle Open URL', { url })
    }

    handleNotification = (notification: any) => {
      if (!notification?.data) {
        return
      }
      if (AppState.currentState === 'active') {
        this.refetchNotifications()
      }
      const notificationData = notification.data

      if (notificationData.kind) {
        if (!notification.isClicked) {
          return
        }

        const userId = get(this.props, 'user.id')
        return handleEngineNotification({
          notificationData,
          userId,
          dispatch: this.props.dispatch,
        })
      }
    }

    handleNotificationToken = (payload: { token: string }) => {
      console.log('AppState:handleNotificationToken:', payload)
      Bugsnag.leaveBreadcrumb('Handle Notification Token', { payload: inspect(payload) })

      const isAuthorized = !!get(this.props, 'user')

      const { isImpersonating } = this.state

      if (!isAuthorized || isImpersonating) {
        return
      }

      const { token } = payload
      const userId = get(this.props, 'user.id')
      const notificationTokenRegisteredAtKey = `${Storage.NOTIFICATION_TOKEN_REGISTERED_AT_KEY}_${userId}`
      const notificationTokenRegisteredAt = Storage.get<string>(notificationTokenRegisteredAtKey)

      if (
        !notificationTokenRegisteredAt ||
        moment().diff(moment(notificationTokenRegisteredAt), 'days') >
          NOTIFICATION_TOKEN_EXPIRY_DAYS
      ) {
        this.props.dispatch({
          type: 'users/registerToken',
          payload: {
            id: userId,
            type: 'fcm',
            token,
          },
          success: () => {
            Storage.set(notificationTokenRegisteredAtKey, moment())
          },
        })

        if (Device.android) {
          Intercom.sendTokenToIntercom(token)
        }
      }
    }

    handleTimeZone = (user: UserType) => {
      const { isImpersonating, timeZone } = this.state

      const newTimeZone = isImpersonating && user.timeZone ? user.timeZone : moment.tz.guess(true)

      this.setTimeZone(newTimeZone)

      if (newTimeZone !== timeZone) {
        this.props.dispatch({
          type: 'app/clearCaches',
          success: () => {
            DeviceEventEmitter.emit('onRefreshPress', {})
          },
        })
      }
    }

    handleQuickAction = (data: any) => {
      console.log('AppState:handleQuickAction:', data)
      Bugsnag.leaveBreadcrumb('Handle Quick Action', { data: inspect(data) })

      if (data) {
        const { url } = data.userInfo

        if (url === `${Config.APP_SCHEME}://app/modals/newMessage`) {
          Intercom.showIntercomMessenger({ source: 'QuickActionMenu' })
        } else {
          openUrl(url)
        }
      }
    }

    setTimeZone = (timeZone: string) => {
      Apollo.setTimeZone(timeZone)

      // Hawaii instead of US/Hawaii
      const momentTimeZone = moment.tz.names().find((name) => name.includes(timeZone)) || timeZone
      moment.tz.setDefault(momentTimeZone)

      this.setState({
        timeZone,
      })
    }

    logFirstLogin = (isImpersonating: boolean, user: UserType) => {
      if (isImpersonating) {
        return
      }

      const firstLoginTimestamp = Storage.get<string>(Storage.APP_FIRST_LOGIN_TIMESTAMP_KEY)

      if (!firstLoginTimestamp) {
        Storage.set(Storage.APP_FIRST_LOGIN_TIMESTAMP_KEY, moment().toISOString())
      }

      const userBasedKey = `${Storage.APP_FIRST_LOGIN_TIMESTAMP_KEY}_${user.id}`
      if (!Storage.get(userBasedKey)) {
        Storage.set(userBasedKey, moment().toISOString())
      }
    }

    updateConsecutiveDaysAppUsage = () => {
      const currentDate = moment().format('YYYY-MM-DD')
      const usageKey = `${Storage.APP_CONSECUTIVE_USAGE_ON_DATE_KEY}_${currentDate}`
      const usageDays = Storage.get<number>(usageKey)

      if (!usageDays) {
        const previousDate = moment().subtract(1, 'day').format('YYYY-MM-DD')
        const previousUsageKey = `${Storage.APP_CONSECUTIVE_USAGE_ON_DATE_KEY}_${previousDate}`
        const previousUsageDays = Storage.get<number>(previousUsageKey) || 0

        Storage.set(usageKey, previousUsageDays + 1)
        Storage.remove(previousUsageKey)
      }
    }

    checkAppVersion = () => {
      const previousAppVersion = Storage.get<string>(Storage.APP_VERSION_KEY)

      const currentAppVersion = App.version

      if (currentAppVersion !== previousAppVersion) {
        this.onAppVersionUpdate()
        Storage.set(Storage.APP_VERSION_KEY, currentAppVersion)
      }
    }

    onAppVersionUpdate = () => {
      const { dispatch } = this.props

      dispatch({ type: 'app/clearCaches' })
      // reset last request status to avoid showing app Outdated version screen
      dispatch({ type: 'app/updateAppState', payload: { lastRequestStatus: null } })
    }

    render() {
      const { timeZone, isImpersonating } = this.state

      return (
        <TimeZoneContext.Provider value={{ timeZone, setTimeZone: this.setTimeZone }}>
          <WebPresentationModeProvider>
            <ImpersonationContext.Provider value={{ isImpersonating }}>
              <ApolloProvider client={Apollo.getClient()}>
                <IntercomProvider>
                  <WrappedComponent {...this.props} />
                </IntercomProvider>
              </ApolloProvider>
            </ImpersonationContext.Provider>
          </WebPresentationModeProvider>
        </TimeZoneContext.Provider>
      )
    }
  }

  const mapStateToProps = (state: RootStoreState) => {
    const visitedTutorials = visitedTutorialsSelector(state)
    const ketoMojoUser = ketoMojoUserSelector(state)
    const user = userSelector(state)
    const sensor = sensorSelector(state)
    const settings = settingsSelector(state)
    const onboarding = onboardingSelector(state)
    const isOnboardingInProgress = onboarding.isInProgress

    return {
      ketoMojoUser,
      visitedTutorials,
      user,
      sensor,
      isOnboardingInProgress,
      healthKitSync: settings.healthKitSync,
      ketoMojoSync: settings.ketoMojoSync,
    }
  }

  return connect(mapStateToProps)(AppWithState as any)
}
